<template>
  <div class="section section--offers">
    <h2 class="section__title">Make Offers to Likers</h2>
    <div class="section__container">
      <div class="section__content" v-if="isLoggedIn">
        <div class="details">
          <div>
            <label>Discount</label>
            <span class="range">
              <span class="range__display">
                <span class="range__display__value">{{discount}}</span>% off
              </span>
              <input
                v-model="discount"
                type="range"
                class="range__input"
                min="10"
                max="90"
                step="5"
              />
            </span>
          </div>
          <div>
            <label>Shipping discount</label>
            <select class="select" v-model="shippingDiscount">
              <option v-for="discount in shippingDiscounts" :value="discount.id" :key="discount.id">
                {{ discount.label }}
              </option>
            </select>
            <!-- <p v-if="selectedShippingDiscount" class="note">{{ selectedShippingDiscount.footer_message }}</p> -->
          </div>
          <div class="filters">
            <a
              class="control control--toggle-filters"
              href="javascript:void(0)"
              @click="showAdvancedFilters = !showAdvancedFilters"
            >
              <span v-if="!showAdvancedFilters">Show filters</span>
              <span v-else>Hide listing filters</span>
            </a>
            <transition>
              <div v-if="showAdvancedFilters">
                <div>
                  <label>Listing Date</label>
                  <select class="select" v-model="dateOperator">
                    <option value="lt">Older than</option>
                    <option default value="gt">Newer than</option>
                  </select>
                  <flat-pickr
                    class="date-input"
                    v-model="dateString"
                    placeholder="Select date"
                    :config="flatpickrConfig"
                  />
                </div>
                <div>
                  <label>Price</label>
                  <select class="select" v-model="priceOperator">
                    <option default value="gt">Greater than</option>
                    <option value="lt">Less than</option>
                  </select>
                  $<input class="input" type="text" inputmode="decimal" v-model="priceFilter" />
                </div>
                <div>
                  <label>Condition</label>
                  <select class="select" v-model="condition">
                    <option default :value="null">All</option>
                    <option value="nwt">New with tags</option>
                    <option value="not_nwt">Not new with tags</option>
                  </select>
                </div>
                <div v-if="allBrands.length > 0">
                  <label>Brand</label>
                  <select class="select" v-model="brandOperator">
                    <option default value="eq">Only brands</option>
                    <option value="neq">Excluding brands</option>
                  </select>
                  <v-select multiple v-model="brands" :options="allBrands" />
                </div>
                <div v-if="catalog">
                  <label>Category</label>
                  <div class="category-select-container">
                    <v-select v-model="department" :options="catalog.departments" label="name" />
                    <transition name="slide-down">
                      <v-select
                        v-if="department"
                        v-model="category"
                        :options="filteredCategories"
                        label="name"
                      />
                    </transition>
                    <transition name="slide-down">
                      <v-select
                        v-if="category"
                        v-model="subCategory"
                        :options="filteredSubCategories"
                      />
                    </transition>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>
        <div class="info">
          <h3 class="info__title">About Offers</h3>
          <p>Make offers to likers for your entire closet, or for listings that are older than a certain date.</p>

          <p>We recommend sending offers often so that any new likers will be encouraged to purchase.</p>

          <p>Offering a larger discount to your older listings is a great way to clear out your closet.</p>
        </div>
        <div class="controls">
          <a
            class="control control--send"
            :class="(!this.hasFilters || this.listingsCount) ? '' : 'control--disabled'"
            @click="makeOffers"
            href="javascript:void(0)"
          >
            Send <strong>{{discount}}% off</strong> offers
            <span v-if="listingsCount !== null">for <strong>{{listingsCount}} listings</strong></span>
          </a>
          <p v-if="likes" class>{{likes}} likers will receive this offer</p>
          <div v-if="typeof(offerStatus.status) !== 'undefined'" class="offer-status">
            <p v-if="offerStatus.status === 'completed'">Finished sending offers!</p>
            <p v-else>
              Sending offers
              <progress :value="offerStatus.progress" max="100"></progress>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from 'vue-select';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import 'vue-select/dist/vue-select.css';

const SHIPPING_DISCOUNTS = [
  {
    id: '5ff7647a5d29bbebfa25f9d0',
    discount_amount: {
      val: '0.0',
      currency_code: 'USD',
      currency_symbol: '$',
    },
    label: 'No Discount',
    enabled: true,
  },
  {
    id: '5ff7647a5d29bbebfa25f9d1',
    discount_amount: {
      val: '1.5',
      currency_code: 'USD',
      currency_symbol: '$',
    },
    label: '$5.95',
    footer_message: 'Seller Shipping Discount: $1.50',
    enabled: true,
  },
  {
    id: '5ff7647a5d29bbebfa25f9d2',
    discount_amount: {
      val: '2.46',
      currency_code: 'USD',
      currency_symbol: '$',
    },
    label: '$4.99',
    footer_message: 'Seller Shipping Discount: $2.46',
    enabled: true,
  },
  {
    id: '5ff7647a5d29bbebfa25f9d3',
    discount_amount: {
      val: '7.45',
      currency_code: 'USD',
      currency_symbol: '$',
    },
    label: 'FREE',
    footer_message: 'Seller Shipping Discount: $7.45',
    enabled: true,
  },
];

export default {
  components: { vSelect, flatPickr },
  data() {
    return {
      discount: 10,
      shippingDiscount: SHIPPING_DISCOUNTS[0].id,
      dateString: '',
      dateOperator: 'lt',
      loading: false,
      listingsCount: null,
      likes: null,
      brands: [],
      brandOperator: 'eq',
      condition: null,
      priceFilter: '',
      priceOperator: 'gt',
      department: null,
      category: null,
      subCategory: null,
      allBrands: [],
      catalog: null,
      showAdvancedFilters: false,
    };
  },
  computed: {
    date() {
      if (this.dateString === '') {
        return null;
      }
      return (new Date(this.dateString)).getTime();
    },
    today() {
      return new Date().toISOString().split('T')[0];
    },
    flatpickrConfig() {
      return {
        maxDate: this.today,
        altInput: true,
        altFormat: 'F j, Y',
        dateFormat: 'Y-m-d',
      };
    },
    isLoggedIn() {
      return this.$store.getters.isLoggedIn;
    },
    offerStatus() {
      return this.$store.state.offerStatus;
    },
    filteredCategories() {
      if (!this.department) {
        return [];
      }
      return this.department.categories;
    },
    filteredSubCategories() {
      if (!this.category) {
        return [];
      }
      return this.category.subCategories;
    },
    filters() {
      return {
        date: this.date,
        dateOperator: this.dateOperator,
        brands: this.brands,
        brandOperator: this.brandOperator,
        condition: this.condition,
        priceFilter: this.priceFilter,
        priceOperator: this.priceOperator,
        department: this.department ? this.department.name : null,
        category: this.category ? this.category.name : null,
        subCategory: this.subCategory,
        count: true,
        sort: 'order',
        order: 'asc',
      };
    },
    hasFilters() {
      return this.date !== null || this.brands.length > 0 || this.condition !== null || this.priceFilter.length > 0 || this.department !== null;
    },
    shippingDiscounts() {
      return SHIPPING_DISCOUNTS;
    },
    selectedShippingDiscount() {
      return this.shippingDiscounts.find((d) => d.id === this.shippingDiscount);
    },
  },
  methods: {
    async fetchListingsCount() {
      const response = await this.$store.dispatch('fetchOfferListingsDetails', this.filters);
      this.listingsCount = response.count;
      this.likes = response.likes;
    },
    makeOffers() {
      if (this.hasFilters && !this.listingsCount) {
        return;
      }
      this.$store.dispatch('makeOffers', {
        discount: this.discount / 100,
        shippingDiscount: this.shippingDiscount,
        filters: this.filters,
      });
    },
    async updateCatalog() {
      this.allBrands = await this.$store.dispatch('fetchBrands');
      this.catalog = await this.$store.dispatch('fetchCatalog');
    },
  },
  created() {
    return this.updateCatalog();
  },
  watch: {
    filters() {
      this.fetchListingsCount();
    },
    department() {
      this.category = null;
      this.subCategory = null;
    },
    category() {
      this.subCategory = null;
    },
  },
};
</script>
